// The path, block and readiness rules for disc/drive content intake (ADR-021).
// One copy, read by the SPA uploader and by the portal API, so a path the
// browser accepts is the path the server records and a session the SPA shows
// as ready is the session the price and submit gates accept.
export const MEDIA_CONTENT_MAX_KEY_LENGTH = 1024;
export const MEDIA_CONTENT_MAX_SEGMENT_LENGTH = 255;
// Room left for `<tenant_id>/<quote_id>/<session_id>/` when the caller does not
// know the exact prefix (the SPA). Three UUIDs and their slashes take 111
// characters, so the browser is never more lenient than the server.
export const MEDIA_CONTENT_PREFIX_ALLOWANCE = 128;
// Characters a FAT/exFAT drive, a disc file system or a Windows workstation
// cannot hold in a name, so content carrying them could never be copied as sent.
const ILLEGAL_NAME_CHARS = /["*:<>?|]/;
function hasControlChar(value) {
    for (let i = 0; i < value.length; i += 1) {
        const code = value.charCodeAt(i);
        if (code <= 0x1f || (code >= 0x7f && code <= 0x9f))
            return true;
    }
    return false;
}
// The customer's own folder path, below `<session_id>/`. It is a deliberate
// exception to ADR-007 Principle 8, confined to this store, so every rule that
// keeps it from escaping its prefix or colliding lives here and nowhere else.
export function normalizeMediaRelativePath(raw, opts = {}) {
    if (typeof raw !== 'string' || raw.length === 0)
        return { ok: false, reason: 'empty' };
    const path = raw.normalize('NFC');
    if (hasControlChar(path))
        return { ok: false, reason: 'control_char' };
    if (path.includes('\\'))
        return { ok: false, reason: 'backslash' };
    if (ILLEGAL_NAME_CHARS.test(path))
        return { ok: false, reason: 'illegal_char' };
    if (path.startsWith('/'))
        return { ok: false, reason: 'absolute' };
    for (const segment of path.split('/')) {
        if (segment.length === 0)
            return { ok: false, reason: 'empty_segment' };
        if (segment === '.' || segment === '..')
            return { ok: false, reason: 'dot_segment' };
        if (segment.endsWith('.'))
            return { ok: false, reason: 'trailing_dot' };
        if (segment !== segment.trimEnd())
            return { ok: false, reason: 'trailing_space' };
        if ([...segment].length > MEDIA_CONTENT_MAX_SEGMENT_LENGTH) {
            return { ok: false, reason: 'segment_too_long' };
        }
    }
    const prefixLength = opts.prefixLength ?? MEDIA_CONTENT_PREFIX_ALLOWANCE;
    if (prefixLength + path.length > MEDIA_CONTENT_MAX_KEY_LENGTH) {
        return { ok: false, reason: 'too_long' };
    }
    return { ok: true, path };
}
// Two manifest paths that differ only by case land on one folder on a FAT/exFAT
// drive or a disc, so a session refuses the pair rather than silently keeping one.
export function mediaPathCollisionKey(path) {
    return path.normalize('NFC').toLowerCase();
}
// Azure requires every block id within one blob to have the same encoded length;
// six zero-padded digits cover the 50,000-block ceiling. Deterministic, so a
// resumed upload re-sends exactly the ids Get Block List reports as missing.
export function mediaContentBlockId(index) {
    if (!Number.isInteger(index) || index < 0 || index > 999_999) {
        throw new RangeError(`block index out of range: ${index}`);
    }
    return btoa(String(index).padStart(6, '0'));
}
export function mediaContentBlockIndex(blockId) {
    let decoded;
    try {
        decoded = atob(blockId);
    }
    catch {
        return null;
    }
    return /^\d{6}$/.test(decoded) ? Number(decoded) : null;
}
const LINK_STATUSES_PASSING_GATE = new Set(['ok', 'unsupported']);
export function mediaLinkStatusPassesGate(status) {
    return status != null && LINK_STATUSES_PASSING_GATE.has(status);
}
// Is a media line's content handed over, per ADR-021 §7? A pure function of
// stored data (R26): the delivery answer and link on the line config, the line's
// latest session and its latest link check. A link check only counts for the
// link it checked, so editing the link after a passing check needs a new check.
export function mediaContentLineReady(args) {
    switch (args.delivery) {
        case 'upload':
            return args.latest_session?.status === 'complete';
        case 'link': {
            const check = args.latest_link_check;
            if (!check || !mediaLinkStatusPassesGate(check.status))
                return false;
            return (args.configured_link ?? '').trim() === check.url.trim();
        }
        case 'mail_master':
        case 'follow_up':
        case 'ship_blank':
            return true;
        default:
            return true;
    }
}
//# sourceMappingURL=media-content.js.map